import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { CategoryWithSubCategorySelect } from './subcomponents/Selectors'
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from '@clerk/clerk-react'
import { useStore } from '../context/StoreContext';
import { useAuthAxios } from './authfetch';
import { apiurl } from '../common/constants';

export default function Navbar(props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoaded, isSignedIn, user } = useUser();
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [activePage, setActivePage] = useState("/")
  const [isScrolled, setIsScrolled] = useState(false)
  const [pendingOrderCount, setPendingOrderCount] = useState(0)
  const { isAdmin, setIsAdmin } = useStore()
  const sidebarRef = useRef(null)
  const authAxios = useAuthAxios()

  // Navigation items configuration
  const navItems = [
    { path: '/home', label: 'Home', key: 'home', matchPaths: ['home', '/', ''] },
    { path: '/brands', label: 'Brands', key: 'brands', matchPaths: ['brands'] },
    { path: '/category', label: 'Categories', key: 'category', matchPaths: ['category'] },
    { path: '/contact', label: 'Contact', key: 'contact', matchPaths: ['contact'] },
    { path: '/recentview', label: 'Recent', key: 'recentview', matchPaths: ['recentview'] },
  ]

  const handleNavClick = () => {
    setIsSidebarOpen(false)
  }

  const isActive = (matchPaths) => {
    return matchPaths.some(p => activePage === p)
  }

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false)
      }
    }

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'unset'
    }
  }, [isSidebarOpen])

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fetch pending order count for admin
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (isAdmin) {
        try {
          const response = await authAxios.get(`${apiurl}/orders/pending/count`)
          setPendingOrderCount(response.data?.data?.count || 0)
        } catch (error) {
          console.error('Error fetching pending order count:', error)
          setPendingOrderCount(0)
        }
      }
    }
    fetchPendingCount()
  }, [isAdmin, authAxios])

  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const role = user.publicMetadata?.role || 'user';
      if (role === 'admin' || role === 'superadmin') {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(false);
    }
    setActivePage(window.location.href.split("/").slice(-1)[0])
  }, [location, isLoaded, isSignedIn, user, setIsAdmin])

  const handleSearch = (e) => {
    e.preventDefault()
    setIsSidebarOpen(false)
    const cat = document.querySelector("#formSearchCategory").value
    let categoryType
    let searchquery = ""
    
    if (cat[0] === "M") {
      categoryType = "Main"
    } else if (cat[0] === "S") {
      categoryType = "Sub"
    } else {
      categoryType = "All"
    }
    searchquery = document.querySelector("#search").value
    navigate(`/search/?q=${searchquery}&categorytype=${categoryType}&ref=${cat.slice(1,)}&p=1`)
  }

  return (
    <>
      {/* Main Navbar */}
      <nav className={`modern-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          {/* Logo */}
          <Link className="navbar-logo" to="/" onClick={handleNavClick}>
            <img src="/logo.png" alt="Planet Music" />
          </Link>

          {/* Desktop Navigation */}
          <ul className="navbar-menu desktop-menu">
            {navItems.map(item => (
              <li key={item.key} className="nav-menu-item">
                <Link
                  className={`nav-menu-link ${isActive(item.matchPaths) ? 'active' : ''}`}
                  to={item.path}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            {isAdmin && (
              <li className="nav-menu-item">
                <Link
                  className={`nav-menu-link admin-link ${activePage === 'adminpanel' ? 'active' : ''}`}
                  to="/adminpanel"
                  style={{ position: 'relative' }}
                >
                  <i className="fa-solid fa-gear"></i>
                  <span>Admin</span>
                  {pendingOrderCount > 0 && (
                    <span className="pending-badge">{pendingOrderCount}</span>
                  )}
                </Link>
              </li>
            )}
          </ul>

          {/* Desktop Search */}
          <form className="navbar-search desktop-search" role="search" onSubmit={handleSearch}>
            <div className="search-wrapper">
              <CategoryWithSubCategorySelect
                selectId="formSearchCategory"
                firstOption="All"
              />
              <div className="search-input-group">
                <input
                  className="search-input"
                  id="search"
                  type="search"
                  placeholder="Search products..."
                  aria-label="Search"
                />
                <button className="search-btn" type="submit">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </button>
              </div>
            </div>
          </form>

          {/* Auth Buttons - Desktop */}
          <div className="navbar-auth desktop-auth">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="auth-btn login-btn">
                  <i className="fa-solid fa-user"></i>
                  <span>Login</span>
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserButton 
                appearance={{
                  elements: {
                    avatarBox: "user-avatar-box"
                  }
                }}
                afterSignOutUrl="/"
              />
            </SignedIn>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className={`mobile-menu-toggle ${isSidebarOpen ? 'active' : ''}`}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle navigation menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      <div className={`sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}></div>

      {/* Mobile Sidebar */}
      <aside
        ref={sidebarRef}
        className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}
      >
        <div className="sidebar-header">
          <Link className="sidebar-logo" to="/" onClick={handleNavClick}>
            <img src="/logo.png" alt="Planet Music" />
          </Link>
          <div className="sidebar-header-right">
            {/* Auth Buttons - Mobile */}
            <div className="sidebar-auth">
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="auth-btn login-btn mobile">
                    <i className="fa-solid fa-user"></i>
                  </button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <UserButton 
                  appearance={{
                    elements: {
                      avatarBox: "user-avatar-box"
                    }
                  }}
                  afterSignOutUrl="/"
                />
              </SignedIn>
            </div>
            <button
              className="sidebar-close"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close menu"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form className="sidebar-search" role="search" onSubmit={handleSearch}>
          <div className="search-input-group mobile">
            <input
              className="search-input"
              type="search"
              placeholder="Search products..."
              aria-label="Search"
              id="search-mobile"
              onChange={(e) => {
                document.querySelector("#search").value = e.target.value
              }}
            />
            <button className="search-btn" type="submit">
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>
          </div>
        </form>

        {/* Mobile Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {navItems.map(item => (
              <li key={item.key} className="sidebar-menu-item">
                <Link
                  className={`sidebar-menu-link ${isActive(item.matchPaths) ? 'active' : ''}`}
                  to={item.path}
                  onClick={handleNavClick}
                >
                  <span className="link-indicator"></span>
                  {item.label}
                </Link>
              </li>
            ))}
            {isAdmin && (
              <li className="sidebar-menu-item">
                <Link
                  className={`sidebar-menu-link admin-link ${activePage === 'adminpanel' ? 'active' : ''}`}
                  to="/adminpanel"
                  onClick={handleNavClick}
                  style={{ position: 'relative' }}
                >
                  <span className="link-indicator"></span>
                  <i className="fa-solid fa-gear"></i>
                  Admin Panel
                  {pendingOrderCount > 0 && (
                    <span className="pending-badge">{pendingOrderCount}</span>
                  )}
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <p className="sidebar-footer-text">
            <i className="fa-solid fa-music"></i>
            Your Music Journey Starts Here
          </p>
        </div>
      </aside>
    </>
  )
}
